import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle, Circle, ArrowRight, Rocket, X, Confetti, Target, Plus, Calendar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// One-tap goal presets so "set a goal" never needs a form.
const GOAL_PRESETS = [
  { title: "Hit a new squat PR", unit: "kg" },
  { title: "Add 5kg to my bench press", target_value: 5, unit: "kg" },
  { title: "Train 4 times this week", target_value: 4, unit: "sessions" },
  { title: "Improve my 5k time", unit: "min" },
  { title: "Stay injury-free this block", unit: "" },
  { title: "Log recovery every day", target_value: 7, unit: "days" },
];

// Friendly, data-driven "get started" checklist for new athletes. Mirrors the
// coach version: every step is computed from real state (GET /athlete/quickstart)
// so it ticks itself off as the athlete trains — no manual marking.
const STEP_META = {
  profile:   { label: "Complete your athlete profile", desc: "Add your sport, focus and stats.", cta: "EDIT PROFILE", tab: "profile" },
  workout:   { label: "Log your first workout",        desc: "Record a session — it takes seconds.", cta: "LOG WORKOUT", to: "/workouts" },
  goal:      { label: "Set a training goal",           desc: "Give your training a target to chase.", cta: "SET A GOAL", tab: "profile" },
  readiness: { label: "Do a readiness check-in",       desc: "Tell us how recovered you feel today.", cta: "CHECK IN", tab: "readiness" },
  coach:     { label: "Connect with your coach",       desc: "Link up to get programs & feedback.", cta: "OPEN CHAT", tab: "coach" },
};
const ORDER = ["profile", "workout", "goal", "readiness", "coach"];

export default function AthleteQuickStart({ onGoTab }) {
  const { user, setUser } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [custom, setCustom] = useState("");
  const [deadline, setDeadline] = useState("");

  const fetchData = () => api.get("/athlete/quickstart")
    .then(({ data }) => setData(data))
    .catch(() => setFailed(true));

  useEffect(() => { fetchData(); }, []);

  const createGoal = async (goal) => {
    if (savingGoal) return;
    setSavingGoal(true);
    try {
      await api.post("/goals", {
        title: goal.title,
        target_value: goal.target_value || 0,
        unit: goal.unit || "",
        deadline: deadline || null,
      });
      toast.success(deadline ? "Goal set — it's on your countdown!" : "Goal set — nice!");
      setGoalOpen(false);
      setCustom("");
      setDeadline("");
      fetchData(); // re-check so the checklist step ticks itself off
      window.dispatchEvent(new Event("goals:changed")); // refresh deadline countdown
    } catch {
      toast.error("Couldn't save goal");
    } finally {
      setSavingGoal(false);
    }
  };

  const dismissed = !!user?.onboarding_dismissed?.athlete_quickstart;
  if (dismissed || failed) return null;
  if (!data) {
    return (
      <div className="tactical-card p-5 mb-6 animate-pulse" data-testid="athlete-quickstart-loading">
        <div className="h-5 w-48 bg-white/10 rounded mb-4" />
        <div className="h-2 w-full bg-white/5 rounded" />
      </div>
    );
  }

  const steps = ORDER.map((k) => ({ key: k, done: !!data.steps?.[k], ...STEP_META[k] }));
  const doneCount = data.done_count ?? steps.filter((s) => s.done).length;
  const total = data.total ?? steps.length;
  const pct = Math.round((doneCount / total) * 100);
  const allDone = doneCount === total;
  const nextStep = steps.find((s) => !s.done);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    setUser((u) => ({
      ...u,
      onboarding_dismissed: { ...(u?.onboarding_dismissed || {}), athlete_quickstart: true },
    }));
    try { await api.post("/onboarding/hint/dismiss", { key: "athlete_quickstart" }); } catch { /* noop */ }
  };

  const StepCTA = ({ s }) => {
    const cls = "shrink-0 inline-flex items-center gap-1 label-tiny text-brand-accent border border-brand-accent/40 px-2.5 py-1.5 hover:bg-brand-accent hover:text-surface-base transition-colors";
    if (s.key === "goal") {
      return (
        <button onClick={() => setGoalOpen(true)} className={cls} data-testid={`athlete-quickstart-cta-${s.key}`}>
          {s.cta} <ArrowRight size={11} weight="bold" />
        </button>
      );
    }
    if (s.to) {
      return (
        <Link to={s.to} className={cls} data-testid={`athlete-quickstart-cta-${s.key}`}>
          {s.cta} <ArrowRight size={11} weight="bold" />
        </Link>
      );
    }
    return (
      <button onClick={() => onGoTab?.(s.tab)} className={cls} data-testid={`athlete-quickstart-cta-${s.key}`}>
        {s.cta} <ArrowRight size={11} weight="bold" />
      </button>
    );
  };

  return (
    <>
    <div className="tactical-card p-5 mb-6 relative overflow-hidden" data-testid="athlete-quickstart">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{ background: "radial-gradient(circle at 15% 0%, var(--brand-accent, #FF6B35), transparent 55%)" }}
      />
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 text-text-secondary hover:text-white transition-colors"
        data-testid="athlete-quickstart-dismiss"
        aria-label="Dismiss checklist"
      >
        <X size={16} weight="bold" />
      </button>

      <div className="relative">
        <div className="flex items-center gap-2 mb-1">
          {allDone ? <Confetti size={16} weight="fill" className="text-heat" /> : <Rocket size={16} weight="fill" className="text-heat" />}
          <span className="label-tiny text-brand-accent">{allDone ? "// YOU'RE ALL SET" : "// WELCOME — GET STARTED"}</span>
        </div>
        <h2 className="font-heading text-2xl mb-1">
          {allDone ? "YOU'RE READY TO ROLL" : "GET SET UP IN 5 QUICK STEPS"}
        </h2>
        <p className="text-xs text-text-secondary mb-4">
          {allDone
            ? "Nice — everything's in place. Dismiss this card to clear it."
            : "Knock these out to get the most from your training. It ticks itself off as you go."}
        </p>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-2 bg-white/10 overflow-hidden rounded-full">
            <div
              className="h-full bg-heat transition-all duration-500 rounded-full"
              style={{ width: `${Math.max(4, pct)}%` }}
              data-testid="athlete-quickstart-progress-bar"
            />
          </div>
          <span className="label-tiny text-heat tabular-nums" data-testid="athlete-quickstart-progress">{doneCount}/{total}</span>
        </div>

        {allDone ? (
          <button onClick={dismiss} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest" data-testid="athlete-quickstart-done-cta">
            CLEAR THIS CARD
          </button>
        ) : (
          <div className="space-y-2">
            {steps.map((s) => (
              <div
                key={s.key}
                className={`flex items-center gap-3 border p-3 transition-colors ${
                  s.done
                    ? "border-green-500/20 bg-green-500/[0.03]"
                    : s.key === nextStep?.key
                    ? "border-heat/40 bg-heat/[0.04]"
                    : "border-slate-200"
                }`}
                data-testid={`athlete-quickstart-step-${s.key}`}
              >
                {s.done
                  ? <CheckCircle size={20} weight="fill" className="text-green-500 shrink-0" />
                  : <Circle size={20} weight="duotone" className="text-text-secondary/50 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-heading ${s.done ? "line-through text-text-secondary" : ""}`}>{s.label}</div>
                  {!s.done && <div className="text-[11px] text-text-secondary mt-0.5">{s.desc}</div>}
                </div>
                {!s.done && <StepCTA s={s} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
      <DialogContent className="max-w-md" data-testid="quick-goal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Target size={18} weight="fill" className="text-heat" /> SET A GOAL
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-text-secondary -mt-1 mb-1">
          Tap one to set it instantly — or write your own below.
        </p>
        <div className="flex items-center gap-2 border border-slate-200 px-3 py-2 mb-1">
          <Calendar size={14} weight="duotone" className="text-brand-accent shrink-0" />
          <label className="label-tiny text-text-secondary shrink-0">TARGET DATE</label>
          <input
            type="date"
            value={deadline}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDeadline(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none"
            data-testid="quick-goal-deadline"
          />
          <span className="label-tiny text-[9px] text-text-secondary shrink-0">OPTIONAL</span>
        </div>
        {deadline && (
          <p className="text-[11px] text-brand-accent mb-1" data-testid="quick-goal-deadline-hint">
            This goal will appear on your countdown.
          </p>
        )}
        <div className="grid grid-cols-1 gap-2">
          {GOAL_PRESETS.map((g) => (
            <button
              key={g.title}
              onClick={() => createGoal(g)}
              disabled={savingGoal}
              className="text-left border border-slate-200 hover:border-heat hover:bg-heat/[0.05] px-3 py-2.5 transition-colors flex items-center gap-2 disabled:opacity-50"
              data-testid={`quick-goal-preset-${g.title.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <Target size={14} weight="duotone" className="text-brand-accent shrink-0" />
              <span className="text-sm font-heading flex-1">{g.title}</span>
              <ArrowRight size={12} weight="bold" className="text-text-secondary" />
            </button>
          ))}
        </div>
        <div className="border-t border-slate-200 pt-3 mt-1">
          <div className="label-tiny text-text-secondary mb-2">OR WRITE YOUR OWN</div>
          <div className="flex gap-2">
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && custom.trim()) createGoal({ title: custom.trim() }); }}
              placeholder="e.g. Deadlift 180kg by spring"
              className="flex-1 bg-transparent border border-slate-200 px-3 py-2 text-sm focus:border-heat outline-none"
              data-testid="quick-goal-custom-input"
            />
            <button
              onClick={() => custom.trim() && createGoal({ title: custom.trim() })}
              disabled={savingGoal || !custom.trim()}
              className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="quick-goal-custom-add"
            >
              <Plus size={12} weight="bold" /> ADD
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
